import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import { Grid, GridItem } from '@deal/components'
import { Link } from 'react-router-dom'
import { useNavigationContext } from '#src/components/Header/StandardHeader/NavigationContext'
import { useSellableKeywordTypeaheadQuery } from '#src/components/Header/common/SellableTypeahead.generated'
import { DepartmentBySlugCategoryInfoComponent } from './DepartmentBySlugCategoryInfo.generated'
import { GenericCategoryBySlugComponent } from './GenericCategoryBySlug.generated'
import { useDebounce } from 'use-debounce'
import { getSellableUrl } from '#src/services/sellable'
import { TouchScrollable } from 'react-scrolllock'
import { FlatDepartment } from '../../../../types'
import autosuggestMatch from 'autosuggest-highlight/match'
import autosuggestParse from 'autosuggest-highlight/parse'
import SearchIcon from '#src/components/Header/StandardHeader/icons/mobile/search.svg'
import ClearIcon from '#src/components/Header/StandardHeader/icons/mobile/clear.svg'
import styles from './styles.css'

interface SearchProps {
  categoryId: string
  displayName: string
  onClose: () => void
  onSetIsSearchActive: (isActive: boolean) => void
}

const Search: React.FC<SearchProps> = ({
  categoryId,
  displayName,
  onClose,
  onSetIsSearchActive
}) => {
  const [inputKeywords, setInputKeywords] = useState('')
  const [queryKeywords, setQueryKeywords] = useState('')

  const debouncedQueryKeywords = useDebounce(queryKeywords, 200)

  useEffect(() => {
    setQueryKeywords(inputKeywords)
    onSetIsSearchActive(inputKeywords.length > 0)
  }, [inputKeywords])

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    setInputKeywords(inputValue)
  }

  const { data } = useSellableKeywordTypeaheadQuery({
    variables: {
      query: {
        prefixSearch: debouncedQueryKeywords[0],
        categoryIds: [categoryId]
      },
      first: 10
    },
    skip: queryKeywords.length === 0
  })

  const results = data?.sellableTypeahead.hits

  return (
    <>
      <div className={styles.search}>
        <SearchIcon className={styles.icon} />
        <input
          title={`Search in ${displayName}`}
          type="search"
          placeholder={`Search in ${displayName}...`}
          onChange={onInputChange}
          onBlur={() => onSetIsSearchActive(false)}
          value={inputKeywords}
          className={styles.input}
        />
        {inputKeywords.length > 0 && (
          <button
            className={styles.clear}
            onClick={() => {
              setInputKeywords('')
            }}
          >
            <ClearIcon />
          </button>
        )}

        {queryKeywords.length > 0 && (
          <>
            <TouchScrollable>
              <div
                className={classNames(styles.results, {
                  [styles.hasResults]: results && results.length > 0
                })}
              >
                <Grid>
                  <GridItem col={2}>
                    {results &&
                      results.map(result => {
                        const keywords = queryKeywords
                        const matches = autosuggestMatch(result.title, keywords)
                        const parts = autosuggestParse(result.title, matches)

                        return (
                          <Link
                            key={result.id}
                            to={getSellableUrl(result.sellable)}
                            onClick={() => {
                              setInputKeywords('')
                              onClose()
                            }}
                            className={styles.resultLink}
                          >
                            <div className={styles.result}>
                              {parts.map(part => {
                                if (part.highlight) {
                                  return <strong key={part.text}>{part.text}</strong>
                                }

                                return <React.Fragment key={part.text}>{part.text}</React.Fragment>
                              })}
                            </div>
                          </Link>
                        )
                      })}
                  </GridItem>
                </Grid>
              </div>
            </TouchScrollable>
          </>
        )}
      </div>
      {results && results.length > 0 && (
        <div className={styles.closeContainer}>
          <div
            className={styles.close}
            onClick={() => {
              setInputKeywords('')
            }}
          >
            <span className={styles.closeLabel}>Cancel</span>
          </div>
        </div>
      )}
    </>
  )
}

interface SearchContainerProps {
  onClose: () => void
  flatDepartments: FlatDepartment[]
  onSetIsSearchActive: (isActive: boolean) => void
}

const SearchContainer: React.FC<SearchContainerProps> = ({
  onSetIsSearchActive,
  flatDepartments,
  onClose
}) => {
  const navContext = useNavigationContext()

  const currentFlatDepartment = flatDepartments.find(
    flatDepartment => flatDepartment.referenceSlug === navContext?.activeDepartmentSlug
  )

  /**
   * If there is no active department, default back to site-wide search using the 'Products'
   * category. For data integrity, we stil need to look up that category ID.
   */
  if (currentFlatDepartment) {
    return (
      <div className={styles.searchContainer}>
        <DepartmentBySlugCategoryInfoComponent
          variables={{
            slug: currentFlatDepartment.slug
          }}
        >
          {({ data }) => {
            if (data?.departmentBySlug) {
              return (
                <Search
                  onClose={onClose}
                  onSetIsSearchActive={onSetIsSearchActive}
                  categoryId={data.departmentBySlug.topLevelCategory.id}
                  displayName={data.departmentBySlug.topLevelCategory.pluralDisplayName}
                />
              )
            }

            return null
          }}
        </DepartmentBySlugCategoryInfoComponent>
      </div>
    )
  } else {
    return (
      <GenericCategoryBySlugComponent>
        {({ data }) => {
          if (data?.categoryBySlug) {
            return (
              <div className={styles.searchContainer}>
                <Search
                  onClose={onClose}
                  onSetIsSearchActive={onSetIsSearchActive}
                  categoryId={data.categoryBySlug.id}
                  displayName={'all departments'}
                />
              </div>
            )
          }

          return null
        }}
      </GenericCategoryBySlugComponent>
    )
  }
}

export default SearchContainer
